/**
 * Pane Relief Calculator Framework (PRC)
 * Shared JavaScript framework for all 12 interactive glazing calculators.
 *
 * Version: 1.0.0
 * Created: February 2, 2026
 *
 * Each calculator section registers itself via PRC.register(id, calculator).
 * The framework handles validation, session recovery, API integration,
 * GTM analytics, spam protection, and display formatting.
 *
 * Usage (from a calculator section's <script> tag):
 *   PRC.register('glass-price', {
 *     config: { ... },
 *     inputs: [ { id: 'width', type: 'number', min: 300, max: 4000, required: true } ],
 *     calculate(values) { return { priceRange: { low: 120, high: 180 } }; },
 *     renderResult(result, container) { ... },
 *     buildPayload(values, result) { return { ... }; }
 *   });
 */
(function () {
  'use strict';

  /* ==========================================================================
     Constants
     ========================================================================== */

  var API_BASE = '';
  var API_KEY = '';
  var SESSION_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes
  var SESSION_KEY_PREFIX = 'prc_';
  var TOAST_DURATION_MS = 5000;
  var RETRY_ATTEMPTS = 2;
  var RETRY_BASE_DELAY_MS = 1000;

  /* ==========================================================================
     Internal State
     ========================================================================== */

  var registry = {};          // id -> calculator definition
  var containers = {};        // id -> DOM container element
  var interactionTracked = {}; // id -> boolean (first interaction only)
  var toastTimer = null;
  var toastElement = null;
  var liveRegion = null;

  /* ==========================================================================
     Utility: Shared Toast Element
     ========================================================================== */

  /**
   * Lazily create and return the shared toast element.
   * Appended to document.body so it floats above all calculators.
   */
  function getToastElement() {
    if (toastElement && document.body.contains(toastElement)) {
      return toastElement;
    }

    toastElement = document.createElement('div');
    toastElement.className = 'prc__toast';
    toastElement.setAttribute('role', 'status');
    toastElement.setAttribute('aria-live', 'polite');
    toastElement.innerHTML =
      '<span class="prc__toast-message"></span>' +
      '<button type="button" class="prc__toast-close" aria-label="Dismiss notification">&times;</button>';

    toastElement.querySelector('.prc__toast-close').addEventListener('click', function () {
      hideToast();
    });

    document.body.appendChild(toastElement);
    return toastElement;
  }

  /**
   * Lazily create and return the shared ARIA live region for screen reader announcements.
   */
  function getLiveRegion() {
    if (liveRegion && document.body.contains(liveRegion)) {
      return liveRegion;
    }

    liveRegion = document.createElement('div');
    liveRegion.className = 'prc__sr-only';
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    // Visually hidden styles applied inline to ensure they are always present
    liveRegion.style.cssText =
      'position:absolute;width:1px;height:1px;padding:0;margin:-1px;' +
      'overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;';
    document.body.appendChild(liveRegion);
    return liveRegion;
  }

  function showToast(message, type) {
    var el = getToastElement();
    el.querySelector('.prc__toast-message').textContent = message;

    // Reset class list, then add modifiers
    el.className = 'prc__toast prc__toast--visible';
    if (type) {
      el.classList.add('prc__toast--' + type);
    }

    if (toastTimer) {
      clearTimeout(toastTimer);
    }
    toastTimer = setTimeout(function () {
      hideToast();
    }, TOAST_DURATION_MS);
  }

  function hideToast() {
    if (toastElement) {
      toastElement.classList.remove('prc__toast--visible');
    }
    if (toastTimer) {
      clearTimeout(toastTimer);
      toastTimer = null;
    }
  }

  /* ==========================================================================
     Utility: Screen Reader Announcements
     ========================================================================== */

  function announce(message) {
    var region = getLiveRegion();
    region.textContent = message;
    setTimeout(function () {
      region.textContent = '';
    }, 1000);
  }

  /* ==========================================================================
     Validation Module
     ========================================================================== */

  var validate = {
    /**
     * Check that a value is non-empty after trimming.
     * @param {*} value
     * @returns {{ valid: boolean, message: string }}
     */
    required: function (value) {
      var v = (value == null) ? '' : String(value).trim();
      return {
        valid: v.length > 0,
        message: 'This field is required'
      };
    },

    /**
     * Check that a value is a number within a given range.
     * @param {*} value
     * @param {number} min
     * @param {number} max
     * @returns {{ valid: boolean, message: string }}
     */
    numericRange: function (value, min, max) {
      var num = parseFloat(value);
      if (isNaN(num)) {
        return { valid: false, message: 'Please enter a valid number' };
      }
      if (num < min || num > max) {
        return { valid: false, message: 'Must be between ' + PRC.format.number(min, 0) + ' and ' + PRC.format.number(max, 0) };
      }
      return { valid: true, message: '' };
    },

    /**
     * Validate width and height dimensions in millimetres.
     * @param {*} width
     * @param {*} height
     * @param {number} minMM - minimum dimension in mm
     * @param {number} maxMM - maximum dimension in mm
     * @returns {{ valid: boolean, message: string, field?: string }}
     */
    dimensions: function (width, height, minMM, maxMM) {
      var w = parseFloat(width);
      var h = parseFloat(height);

      if (isNaN(w) || w < minMM || w > maxMM) {
        return {
          valid: false,
          message: 'Width must be between ' + minMM + 'mm and ' + maxMM + 'mm',
          field: 'width'
        };
      }
      if (isNaN(h) || h < minMM || h > maxMM) {
        return {
          valid: false,
          message: 'Height must be between ' + minMM + 'mm and ' + maxMM + 'mm',
          field: 'height'
        };
      }
      return { valid: true, message: '' };
    },

    /**
     * Validate UK postcode format.
     * @param {string} value
     * @returns {{ valid: boolean, message: string }}
     */
    postcode: function (value) {
      var cleaned = String(value || '').trim().toUpperCase();
      // UK postcode: A9 9AA, A99 9AA, A9A 9AA, AA9 9AA, AA99 9AA, AA9A 9AA
      var pattern = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/;
      return {
        valid: pattern.test(cleaned),
        message: 'Please enter a valid UK postcode (e.g. BS1 4DJ)'
      };
    },

    /**
     * Validate email format.
     * @param {string} value
     * @returns {{ valid: boolean, message: string }}
     */
    email: function (value) {
      var pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return {
        valid: pattern.test(String(value || '').trim()),
        message: 'Please enter a valid email address'
      };
    },

    /**
     * Validate UK phone number format.
     * @param {string} value
     * @returns {{ valid: boolean, message: string }}
     */
    phone: function (value) {
      var cleaned = String(value || '').replace(/[\s\-\(\)]/g, '');
      var pattern = /^(\+44|0)[1-9]\d{8,10}$/;
      return {
        valid: pattern.test(cleaned),
        message: 'Please enter a valid UK phone number'
      };
    }
  };

  /* ==========================================================================
     UI Module
     ========================================================================== */

  var ui = {
    /**
     * Show a validation error on a specific field within a calculator container.
     * Looks for an element with [data-prc-error="fieldId"] or id="${calcId}-${fieldId}-error".
     * @param {string} fieldId - the input's id (calculator-scoped, e.g. 'width')
     * @param {string} message
     * @param {string} [calcId] - optional calculator id to scope the query
     */
    showFieldError: function (fieldId, message, calcId) {
      var container = calcId ? containers[calcId] : document;
      if (!container) return;

      var input = container.querySelector('#' + fieldId) ||
                  container.querySelector('[data-prc-input="' + fieldId + '"]');
      if (input) {
        input.classList.add('prc__input--error');
        input.setAttribute('aria-invalid', 'true');
      }

      var errorEl = container.querySelector('[data-prc-error="' + fieldId + '"]') ||
                    container.querySelector('#' + fieldId + '-error');
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.classList.add('prc__error--visible');
      }
    },

    /**
     * Clear the error state on a specific field.
     * @param {string} fieldId
     * @param {string} [calcId]
     */
    clearFieldError: function (fieldId, calcId) {
      var container = calcId ? containers[calcId] : document;
      if (!container) return;

      var input = container.querySelector('#' + fieldId) ||
                  container.querySelector('[data-prc-input="' + fieldId + '"]');
      if (input) {
        input.classList.remove('prc__input--error');
        input.removeAttribute('aria-invalid');
      }

      var errorEl = container.querySelector('[data-prc-error="' + fieldId + '"]') ||
                    container.querySelector('#' + fieldId + '-error');
      if (errorEl) {
        errorEl.textContent = '';
        errorEl.classList.remove('prc__error--visible');
      }
    },

    /**
     * Clear all error states within a calculator.
     * @param {string} calcId
     */
    clearAllErrors: function (calcId) {
      var container = containers[calcId];
      if (!container) return;

      var errorInputs = container.querySelectorAll('.prc__input--error');
      for (var i = 0; i < errorInputs.length; i++) {
        errorInputs[i].classList.remove('prc__input--error');
        errorInputs[i].removeAttribute('aria-invalid');
      }

      var errorMessages = container.querySelectorAll('.prc__error--visible');
      for (var j = 0; j < errorMessages.length; j++) {
        errorMessages[j].textContent = '';
        errorMessages[j].classList.remove('prc__error--visible');
      }
    },

    /**
     * Show a specific screen within a calculator (form, result, error, success).
     * Screens are identified by [data-prc-screen="${screenName}"] within the container.
     * Auto-scrolls to the calculator and moves focus to the first heading when
     * switching away from the form screen (result/error/success).
     * @param {string} calcId
     * @param {string} screen - one of: 'form', 'result', 'error', 'success'
     */
    showScreen: function (calcId, screen) {
      var container = containers[calcId];
      if (!container) return;

      var activeScreenEl = null;
      var screens = container.querySelectorAll('[data-prc-screen]');
      for (var i = 0; i < screens.length; i++) {
        var screenEl = screens[i];
        if (screenEl.getAttribute('data-prc-screen') === screen) {
          screenEl.style.display = '';
          screenEl.classList.add('prc__screen--visible');
          screenEl.removeAttribute('hidden');
          activeScreenEl = screenEl;
        } else {
          screenEl.style.display = 'none';
          screenEl.classList.remove('prc__screen--visible');
          screenEl.setAttribute('hidden', '');
        }
      }

      // Scroll to calculator and focus the first heading on non-form screens
      if (screen !== 'form' && activeScreenEl) {
        // Small delay lets the DOM update complete before scrolling
        setTimeout(function () {
          container.scrollIntoView({ behavior: 'smooth', block: 'start' });

          // Move focus to the first heading or focusable element in the new screen
          var focusTarget = activeScreenEl.querySelector('h2, h3, [data-prc-screen-title]');
          if (focusTarget) {
            // Make heading focusable without adding it to the tab order permanently
            if (!focusTarget.hasAttribute('tabindex')) {
              focusTarget.setAttribute('tabindex', '-1');
            }
            focusTarget.focus({ preventScroll: true });
          }
        }, 80);
      }
    },

    /**
     * Show a toast notification.
     * @param {string} message
     * @param {string} [type] - 'success', 'error', 'info', 'restored'
     */
    showToast: showToast,

    /**
     * Make an announcement for screen readers.
     * @param {string} message
     */
    announce: announce,

    /**
     * Toggle loading state on the calculate button.
     * Expects button to have [data-prc-calculate] and optional child elements
     * with [data-prc-btn-text] and [data-prc-btn-loading].
     * @param {string} calcId
     * @param {boolean} isLoading
     */
    setLoading: function (calcId, isLoading) {
      var container = containers[calcId];
      if (!container) return;

      var btn = container.querySelector('[data-prc-calculate]');
      if (!btn) return;

      if (isLoading) {
        btn.classList.add('prc__calculate-btn--loading');
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
      } else {
        btn.classList.remove('prc__calculate-btn--loading');
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
      }

      var textEl = btn.querySelector('[data-prc-btn-text]');
      var loadingEl = btn.querySelector('[data-prc-btn-loading]');

      if (textEl) textEl.style.display = isLoading ? 'none' : '';
      if (loadingEl) loadingEl.style.display = isLoading ? 'inline' : 'none';
    }
  };

  /* ==========================================================================
     Session Storage Module (Crash Recovery, 30-min Expiry)
     ========================================================================== */

  var session = {
    /**
     * Save calculator input data to sessionStorage.
     * @param {string} calcId
     * @param {Object} data - key/value pairs of input values
     */
    save: function (calcId, data) {
      try {
        var key = SESSION_KEY_PREFIX + calcId + '_data';
        var payload = {
          values: data,
          timestamp: Date.now()
        };
        sessionStorage.setItem(key, JSON.stringify(payload));
      } catch (e) {
        // sessionStorage unavailable or full — skip silently
      }
    },

    /**
     * Restore calculator input data from sessionStorage.
     * Returns null if no data, expired (>30 min), or storage unavailable.
     * @param {string} calcId
     * @returns {Object|null} - the values object, or null
     */
    restore: function (calcId) {
      try {
        var key = SESSION_KEY_PREFIX + calcId + '_data';
        var raw = sessionStorage.getItem(key);
        if (!raw) return null;

        var payload = JSON.parse(raw);
        if (!payload || !payload.timestamp || !payload.values) return null;

        if (Date.now() - payload.timestamp > SESSION_EXPIRY_MS) {
          session.clear(calcId);
          return null;
        }

        return payload.values;
      } catch (e) {
        return null;
      }
    },

    /**
     * Clear stored session data for a calculator.
     * @param {string} calcId
     */
    clear: function (calcId) {
      try {
        var key = SESSION_KEY_PREFIX + calcId + '_data';
        sessionStorage.removeItem(key);
      } catch (e) {
        // skip silently
      }
    }
  };

  /* ==========================================================================
     API Integration Module
     ========================================================================== */

  /**
   * Internal fetch wrapper with retry and exponential backoff.
   * @param {string} url
   * @param {Object} payload
   * @returns {Promise<Object>}
   */
  async function fetchWithRetry(url, payload) {
    var lastError;
    var headers = { 'Content-Type': 'application/json' };
    if (API_KEY) headers['X-API-Key'] = API_KEY;

    for (var attempt = 0; attempt <= RETRY_ATTEMPTS; attempt++) {
      try {
        var response = await fetch(url, {
          method: 'POST',
          headers: headers,
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          var errorBody = {};
          try { errorBody = await response.json(); } catch (_) { /* ignore parse errors */ }
          throw new Error(errorBody.message || errorBody.error || ('HTTP ' + response.status));
        }

        return await response.json();
      } catch (error) {
        lastError = error;

        if (attempt < RETRY_ATTEMPTS) {
          // Exponential backoff: 1s, 2s
          await new Promise(function (resolve) {
            setTimeout(resolve, RETRY_BASE_DELAY_MS * (attempt + 1));
          });
        }
      }
    }

    throw lastError;
  }

  var api = {
    /**
     * Submit a quote that creates a Shopify draft order.
     * @param {Object} payload
     * @returns {Promise<Object>}
     */
    submitQuote: function (payload) {
      return fetchWithRetry(API_BASE + '/create-draft-order', payload);
    },

    /**
     * Submit a calculator result for lead-only capture.
     *
     * Railway has no /calculator-result route (it never existed; the browser's
     * CORS preflight gets a 404, so these leads were lost and the visitor saw
     * the error screen). Send them as a service lead to /create-draft-order,
     * the same path the service quote form uses: it records a draft order,
     * emails the lead to the business and acknowledges the customer.
     * @param {Object} payload  the calculator's flat buildPayload() result
     * @returns {Promise<Object>}
     */
    submitCalculation: function (payload) {
      return fetchWithRetry(API_BASE + '/create-draft-order', toServiceLead(payload));
    }
  };

  var LEAD_SKIP_KEYS = ['first_name', 'last_name', 'email', 'phone', 'postcode', 'source',
    'submitted_at', 'quote_type', 'calculator', 'gdpr_consent'];

  /**
   * Convert a flat calculator payload into the /create-draft-order service shape.
   * Pure: returns a new object, never mutates the input.
   * @param {Object} p
   * @returns {Object}
   */
  function toServiceLead(p) {
    var name = String(p.calculator || 'calculator').replace(/-/g, ' ');
    var label = 'Calculator: ' + name.charAt(0).toUpperCase() + name.slice(1);
    var lines = [label + ' result'];
    Object.keys(p).forEach(function (k) {
      var v = p[k];
      if (LEAD_SKIP_KEYS.indexOf(k) !== -1 || v === null || v === undefined || v === '' || typeof v === 'object') return;
      lines.push(k.replace(/[_-]/g, ' ') + ': ' + v);
    });
    var config = {
      'Service Type': label,
      'Service Category': label,
      'Phone': p.phone || '',
      'Work Description': lines.join('\n')
    };
    if (p.postcode) config['Postcode'] = String(p.postcode);
    return {
      quote_type: 'service',
      customer: { first_name: p.first_name || '', last_name: p.last_name || '', email: p.email || '' },
      config: config,
      tags: ['calculator-lead', 'calculator-' + String(p.calculator || 'unknown')],
      source: p.source || 'prc-calculator',
      submitted_at: p.submitted_at
    };
  }

  /* ==========================================================================
     GTM Analytics Module
     ========================================================================== */

  function pushToDataLayer(eventName, data) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({
      event: eventName,
      page_url: window.location.pathname
    }, data));
  }

  var analytics = {
    /**
     * Track when a calculator becomes visible / is loaded on the page.
     * @param {string} calcId
     */
    trackView: function (calcId) {
      pushToDataLayer('calculator_view', {
        calculator_name: calcId
      });
    },

    /**
     * Track the first user interaction with a calculator (focus/change on any input).
     * Only fires once per calculator per page load.
     * @param {string} calcId
     */
    trackInteraction: function (calcId) {
      if (interactionTracked[calcId]) return;
      interactionTracked[calcId] = true;

      pushToDataLayer('calculator_interaction', {
        calculator_name: calcId
      });
    },

    /**
     * Track a completed calculation.
     * @param {string} calcId
     * @param {Object} summary - calculator-specific result summary
     */
    trackCalculation: function (calcId, summary) {
      pushToDataLayer('calculator_complete', {
        calculator_name: calcId,
        calculator_summary: summary
      });
    },

    /**
     * Track a lead capture form submission from a calculator.
     * @param {string} calcId
     * @param {string} method - e.g. 'email', 'phone', 'draft_order'
     */
    trackLeadCapture: function (calcId, method) {
      pushToDataLayer('calculator_lead_capture', {
        calculator_name: calcId,
        capture_method: method
      });
    },

    /**
     * Track an error within a calculator.
     * @param {string} calcId
     * @param {string} errorType - e.g. 'validation', 'api', 'calculation'
     */
    trackError: function (calcId, errorType) {
      pushToDataLayer('calculator_error', {
        calculator_name: calcId,
        error_type: errorType
      });
    }
  };

  /* ==========================================================================
     Spam Protection Module
     ========================================================================== */

  var spam = {
    /**
     * Check if a form submission looks like a bot (honeypot field filled).
     * Expects a hidden input with [data-prc-honeypot] inside the form.
     * @param {HTMLFormElement} formElement
     * @returns {boolean} - true if likely a bot
     */
    isBot: function (formElement) {
      if (!formElement) return false;
      var honeypot = formElement.querySelector('[data-prc-honeypot]');
      if (honeypot && honeypot.value) {
        return true;
      }
      return false;
    }
  };

  /* ==========================================================================
     Display Formatting Module
     ========================================================================== */

  var format = {
    /**
     * Format a number as GBP currency. No pence unless fractional.
     * e.g. 120 -> "£120", 120.50 -> "£120.50"
     * @param {number} value
     * @returns {string}
     */
    currency: function (value) {
      var num = parseFloat(value);
      if (isNaN(num)) return '£0';
      // Show pence only if there is a fractional part
      if (num === Math.floor(num)) {
        return '\u00A3' + num.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      }
      return '\u00A3' + num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    /**
     * Format a price range.
     * e.g. (120, 180) -> "£120 – £180"
     * @param {number} low
     * @param {number} high
     * @returns {string}
     */
    currencyRange: function (low, high) {
      return format.currency(low) + ' \u2013 ' + format.currency(high);
    },

    /**
     * Format a number with grouping and optional decimals.
     * e.g. 1234 -> "1,234"
     * @param {number} value
     * @param {number} [decimals=0]
     * @returns {string}
     */
    number: function (value, decimals) {
      var num = parseFloat(value);
      if (isNaN(num)) return '0';
      var d = (decimals != null) ? decimals : 0;
      return num.toLocaleString('en-GB', { minimumFractionDigits: d, maximumFractionDigits: d });
    },

    /**
     * Format a percentage.
     * e.g. 61 -> "61%"
     * @param {number} value
     * @returns {string}
     */
    percent: function (value) {
      var num = parseFloat(value);
      if (isNaN(num)) return '0%';
      return Math.round(num) + '%';
    },

    /**
     * Format a weight in kilograms.
     * e.g. 12.5 -> "12.5 kg"
     * @param {number} kg
     * @returns {string}
     */
    weight: function (kg) {
      var num = parseFloat(kg);
      if (isNaN(num)) return '0 kg';
      // Show 1 decimal place if fractional, 0 if whole
      if (num === Math.floor(num)) {
        return num.toLocaleString('en-GB') + ' kg';
      }
      return num.toLocaleString('en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' kg';
    },

    /**
     * Format a duration in working days.
     * e.g. 5 -> "5 working days", could also handle ranges if given as string "5-7".
     * @param {number|string} days
     * @returns {string}
     */
    duration: function (days) {
      var str = String(days);
      // If it looks like a range (e.g. "5-7" or "5 - 7"), format with en dash
      if (/^\d+\s*[-–]\s*\d+$/.test(str)) {
        var parts = str.split(/\s*[-–]\s*/);
        return parts[0] + '\u2013' + parts[1] + ' working days';
      }
      var num = parseInt(days, 10);
      if (isNaN(num)) return String(days);
      if (num === 1) return '1 working day';
      return num + ' working days';
    },

    /**
     * Format an area in square metres.
     * e.g. 1.68 -> "1.68 m²"
     * @param {number} sqm
     * @returns {string}
     */
    area: function (sqm) {
      var num = parseFloat(sqm);
      if (isNaN(num)) return '0 m\u00B2';
      return num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' m\u00B2';
    }
  };

  /* ==========================================================================
     Registration & Wiring
     ========================================================================== */

  /**
   * Gather current input values from the calculator's container.
   * Reads from elements matching [data-prc-input] within the container.
   * @param {string} calcId
   * @returns {Object} - key/value map of input id -> value
   */
  function gatherValues(calcId) {
    var container = containers[calcId];
    if (!container) return {};

    var values = {};
    var inputs = container.querySelectorAll('[data-prc-input]');
    for (var i = 0; i < inputs.length; i++) {
      var el = inputs[i];
      var key = el.getAttribute('data-prc-input');
      if (el.type === 'checkbox') {
        values[key] = el.checked;
      } else if (el.type === 'radio') {
        if (el.checked) {
          values[key] = el.value;
        }
      } else {
        values[key] = el.value;
      }
    }
    return values;
  }

  /**
   * Populate input fields from a values object (for session restore).
   * @param {string} calcId
   * @param {Object} values
   */
  function populateValues(calcId, values) {
    var container = containers[calcId];
    if (!container || !values) return;

    var inputs = container.querySelectorAll('[data-prc-input]');
    for (var i = 0; i < inputs.length; i++) {
      var el = inputs[i];
      var key = el.getAttribute('data-prc-input');
      if (!(key in values)) continue;

      if (el.type === 'checkbox') {
        el.checked = !!values[key];
      } else if (el.type === 'radio') {
        el.checked = (el.value === values[key]);
      } else {
        el.value = values[key];
      }
    }
  }

  /**
   * Validate all registered inputs for a calculator based on its inputs[] config.
   * @param {string} calcId
   * @param {Object} values - gathered input values
   * @returns {{ valid: boolean, firstErrorField: string|null }}
   */
  function validateInputs(calcId, values) {
    var calc = registry[calcId];
    if (!calc || !calc.inputs) return { valid: true, firstErrorField: null };

    var allValid = true;
    var firstErrorField = null;

    ui.clearAllErrors(calcId);

    for (var i = 0; i < calc.inputs.length; i++) {
      var input = calc.inputs[i];
      var value = values[input.id];
      var result = null;

      // Required check
      if (input.required) {
        result = validate.required(value);
        if (!result.valid) {
          ui.showFieldError(input.id, result.message, calcId);
          allValid = false;
          if (!firstErrorField) firstErrorField = input.id;
          continue;
        }
      } else if (value == null || String(value).trim() === '') {
        // Not required and empty — skip further validation
        continue;
      }

      // Type-specific validation
      if (input.type === 'number' && input.min != null && input.max != null) {
        result = validate.numericRange(value, input.min, input.max);
        if (!result.valid) {
          ui.showFieldError(input.id, result.message, calcId);
          allValid = false;
          if (!firstErrorField) firstErrorField = input.id;
          continue;
        }
      }

      if (input.type === 'email') {
        result = validate.email(value);
        if (!result.valid) {
          ui.showFieldError(input.id, result.message, calcId);
          allValid = false;
          if (!firstErrorField) firstErrorField = input.id;
          continue;
        }
      }

      if (input.type === 'phone') {
        result = validate.phone(value);
        if (!result.valid) {
          ui.showFieldError(input.id, result.message, calcId);
          allValid = false;
          if (!firstErrorField) firstErrorField = input.id;
          continue;
        }
      }

      if (input.type === 'postcode') {
        result = validate.postcode(value);
        if (!result.valid) {
          ui.showFieldError(input.id, result.message, calcId);
          allValid = false;
          if (!firstErrorField) firstErrorField = input.id;
          continue;
        }
      }

      // Custom validator (accepts { valid, message } or error string or null)
      if (typeof input.validate === 'function') {
        result = input.validate(value, values);
        if (typeof result === 'string') {
          result = { valid: false, message: result };
        }
        if (result && !result.valid) {
          ui.showFieldError(input.id, result.message, calcId);
          allValid = false;
          if (!firstErrorField) firstErrorField = input.id;
          continue;
        }
      }
    }

    return { valid: allValid, firstErrorField: firstErrorField };
  }

  /**
   * Auto-save current values on input/change events.
   * @param {string} calcId
   */
  function autoSave(calcId) {
    var values = gatherValues(calcId);
    session.save(calcId, values);
  }

  /**
   * Handle the Calculate button click.
   * Validates -> calculates -> renders result -> shows result screen -> tracks.
   * @param {string} calcId
   */
  function handleCalculate(calcId) {
    var calc = registry[calcId];
    if (!calc) return;

    var values = gatherValues(calcId);

    // Validate
    var validation = validateInputs(calcId, values);
    if (!validation.valid) {
      // Focus the first error field
      if (validation.firstErrorField) {
        var container = containers[calcId];
        var errorField = container.querySelector('#' + validation.firstErrorField) ||
                         container.querySelector('[data-prc-input="' + validation.firstErrorField + '"]');
        if (errorField) {
          errorField.focus();
          errorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      analytics.trackError(calcId, 'validation');
      announce('Please correct the errors before calculating.');
      return;
    }

    // Calculate
    var result;
    try {
      result = calc.calculate(values);
    } catch (err) {
      analytics.trackError(calcId, 'calculation');
      ui.showScreen(calcId, 'error');
      announce('An error occurred during calculation. Please try again.');
      return;
    }

    // Render result
    if (typeof calc.renderResult === 'function') {
      var resultContainer = containers[calcId].querySelector('[data-prc-screen="result"]');
      if (resultContainer) {
        try {
          calc.renderResult(result, resultContainer);
        } catch (err) {
          analytics.trackError(calcId, 'render');
          ui.showScreen(calcId, 'error');
          return;
        }
      }
    }

    // Show result screen
    ui.showScreen(calcId, 'result');
    announce('Calculation complete. Results are now displayed.');

    // Store result on the calculator definition for buildPayload access
    calc._lastResult = result;
    calc._lastValues = values;

    // Track the calculation
    var summary = result;
    if (result && result.priceRange) {
      summary = {
        price_low: result.priceRange.low,
        price_high: result.priceRange.high
      };
    }
    analytics.trackCalculation(calcId, summary);
  }

  /**
   * Handle the lead capture form submission within a calculator.
   * Validates lead fields -> builds payload -> submits via API -> shows success -> tracks.
   * @param {string} calcId
   * @param {Event} event
   */
  async function handleLeadCapture(calcId, event) {
    event.preventDefault();

    var calc = registry[calcId];
    if (!calc) return;

    var container = containers[calcId];
    var leadForm = container.querySelector('[data-prc-lead-form]');

    // Spam check
    if (leadForm && spam.isBot(leadForm)) {
      // Silently fake success for bots
      ui.showScreen(calcId, 'success');
      return;
    }

    // Gather all values (calculation inputs + lead capture fields)
    var values = gatherValues(calcId);

    // Validate lead capture inputs (those marked with data-prc-lead-input)
    var leadValid = true;
    var firstLeadError = null;
    var leadInputs = container.querySelectorAll('[data-prc-lead-input]');

    for (var i = 0; i < leadInputs.length; i++) {
      var el = leadInputs[i];
      var fieldId = el.getAttribute('data-prc-lead-input');
      var isCheckbox = (el.type === 'checkbox');
      var fieldValue = isCheckbox ? (el.checked ? 'checked' : '') : el.value;
      var fieldType = el.getAttribute('data-prc-lead-type') || el.type;
      var fieldRequired = el.hasAttribute('required') || el.getAttribute('data-prc-lead-required') === 'true';

      // Clear previous errors
      ui.clearFieldError(fieldId, calcId);

      // Include lead field values in values object (boolean for checkboxes)
      values[fieldId] = isCheckbox ? el.checked : fieldValue;

      // Required check
      if (fieldRequired) {
        if (isCheckbox && !el.checked) {
          ui.showFieldError(fieldId, 'Please tick this box to continue', calcId);
          leadValid = false;
          if (!firstLeadError) firstLeadError = fieldId;
          continue;
        }
        var req = validate.required(fieldValue);
        if (!req.valid) {
          ui.showFieldError(fieldId, req.message, calcId);
          leadValid = false;
          if (!firstLeadError) firstLeadError = fieldId;
          continue;
        }
      } else if (!fieldValue || !fieldValue.trim()) {
        continue;
      }

      // Type-specific
      if (fieldType === 'email') {
        var emailResult = validate.email(fieldValue);
        if (!emailResult.valid) {
          ui.showFieldError(fieldId, emailResult.message, calcId);
          leadValid = false;
          if (!firstLeadError) firstLeadError = fieldId;
        }
      } else if (fieldType === 'tel' || fieldType === 'phone') {
        var phoneResult = validate.phone(fieldValue);
        if (!phoneResult.valid) {
          ui.showFieldError(fieldId, phoneResult.message, calcId);
          leadValid = false;
          if (!firstLeadError) firstLeadError = fieldId;
        }
      }
    }

    if (!leadValid) {
      if (firstLeadError) {
        var errEl = container.querySelector('#' + firstLeadError) ||
                    container.querySelector('[data-prc-lead-input="' + firstLeadError + '"]');
        if (errEl) {
          errEl.focus();
          errEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      analytics.trackError(calcId, 'lead_validation');
      return;
    }

    // Build payload
    var payload;
    if (typeof calc.buildPayload === 'function') {
      payload = calc.buildPayload(values, calc._lastResult || {});
    } else {
      payload = {
        quote_type: 'calculator',
        calculator: calcId,
        values: values,
        source: 'prc-calculator',
        submitted_at: new Date().toISOString()
      };
    }

    // Determine API endpoint
    var useDraftOrder = calc.config && calc.config.createsDraftOrder;

    // Show loading
    var submitBtn = container.querySelector('[data-prc-lead-submit]');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.classList.add('prc__btn--loading');
      submitBtn.setAttribute('aria-busy', 'true');
    }

    try {
      var response;
      if (useDraftOrder) {
        response = await api.submitQuote(payload);
      } else {
        response = await api.submitCalculation(payload);
      }

      // Clear session data
      session.clear(calcId);

      // Show success screen
      ui.showScreen(calcId, 'success');
      announce('Your details have been submitted successfully.');

      // Populate reference if present
      var refEl = container.querySelector('[data-prc-reference]');
      if (refEl && response) {
        refEl.textContent = response.order_name || response.reference || response.id || '';
      }

      // Track lead capture
      analytics.trackLeadCapture(calcId, useDraftOrder ? 'draft_order' : 'lead_capture');

    } catch (err) {
      analytics.trackError(calcId, 'api');
      ui.showScreen(calcId, 'error');
      announce('There was a problem submitting your details. Please try again.');

      // If there is an error message element, populate it
      var errMsg = container.querySelector('[data-prc-error-message]');
      if (errMsg) {
        errMsg.textContent = err.message || 'Something went wrong. Please try again or call us directly.';
      }
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.classList.remove('prc__btn--loading');
        submitBtn.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * Bind a "Recalculate" / "Back to form" button if present.
   * @param {string} calcId
   */
  function bindRecalculate(calcId) {
    var container = containers[calcId];
    if (!container) return;

    var backBtns = container.querySelectorAll('[data-prc-back]');
    for (var i = 0; i < backBtns.length; i++) {
      backBtns[i].addEventListener('click', function () {
        ui.showScreen(calcId, 'form');
      });
    }
  }

  /**
   * Bind a retry button in the error screen.
   * @param {string} calcId
   */
  function bindRetry(calcId) {
    var container = containers[calcId];
    if (!container) return;

    var retryBtns = container.querySelectorAll('[data-prc-retry]');
    for (var i = 0; i < retryBtns.length; i++) {
      retryBtns[i].addEventListener('click', function () {
        ui.showScreen(calcId, 'form');
      });
    }
  }

  /**
   * Wire up all event listeners for a registered calculator.
   * Called from PRC.register() after storing the calculator in the registry.
   * @param {string} calcId
   */
  function wireCalculator(calcId) {
    var container = containers[calcId];
    var calc = registry[calcId];
    if (!container || !calc) return;

    // 1. Bind calculate button
    var calcBtn = container.querySelector('[data-prc-calculate]');
    if (calcBtn) {
      calcBtn.addEventListener('click', function (e) {
        e.preventDefault();
        handleCalculate(calcId);
      });
    }

    // Also allow form submit to trigger calculation (if calculate button is inside a form)
    var calcForm = container.querySelector('[data-prc-calc-form]');
    if (calcForm) {
      calcForm.addEventListener('submit', function (e) {
        e.preventDefault();
        handleCalculate(calcId);
      });
    }

    // 2. Bind lead capture form
    var leadForm = container.querySelector('[data-prc-lead-form]');
    if (leadForm) {
      leadForm.addEventListener('submit', function (e) {
        handleLeadCapture(calcId, e);
      });
    }

    // 3. Bind back / recalculate buttons
    bindRecalculate(calcId);

    // 4. Bind retry buttons
    bindRetry(calcId);

    // 5. Restore session data
    var savedValues = session.restore(calcId);
    if (savedValues) {
      populateValues(calcId, savedValues);
      showToast('Your previous inputs have been restored', 'restored');
    }

    // 6. Track calculator view
    analytics.trackView(calcId);

    // 7. Track first interaction on any input focus or change
    var allInputs = container.querySelectorAll('input, select, textarea');
    var interactionHandler = function () {
      analytics.trackInteraction(calcId);
    };
    for (var i = 0; i < allInputs.length; i++) {
      allInputs[i].addEventListener('focus', interactionHandler, { once: true });
      allInputs[i].addEventListener('change', interactionHandler, { once: true });
    }

    // 8. Auto-save on input/change
    container.addEventListener('input', function () {
      autoSave(calcId);
    });
    container.addEventListener('change', function () {
      autoSave(calcId);
    });

    // 9. Clear field errors on input
    var errorClearInputs = container.querySelectorAll('[data-prc-input], [data-prc-lead-input]');
    for (var j = 0; j < errorClearInputs.length; j++) {
      (function (el) {
        var fieldId = el.getAttribute('data-prc-input') || el.getAttribute('data-prc-lead-input');
        el.addEventListener('input', function () {
          ui.clearFieldError(fieldId, calcId);
        });
      })(errorClearInputs[j]);
    }

    // 10. Show the form screen by default
    ui.showScreen(calcId, 'form');
  }

  /* ==========================================================================
     Public API: window.PRC
     ========================================================================== */

  var PRC = {
    /**
     * Register a calculator with the framework.
     * The section's container element must have data-prc-calculator="${id}".
     *
     * @param {string} id - unique calculator identifier (e.g. 'glass-price')
     * @param {Object} calculator - calculator definition:
     *   @param {Object} [calculator.config] - calculator-specific configuration
     *   @param {Array}  [calculator.inputs] - input definitions for validation
     *   @param {Function} calculator.calculate - (values) => result
     *   @param {Function} [calculator.renderResult] - (result, containerEl) => void
     *   @param {Function} [calculator.buildPayload] - (values, result) => payload
     */
    register: function (id, calculator) {
      if (!id || !calculator) {
        console.warn('[PRC] register() requires an id and calculator definition.');
        return;
      }

      // Store in registry
      registry[id] = calculator;

      // Find the DOM container
      var container = document.querySelector('[data-prc-calculator="' + id + '"]');
      if (!container) {
        console.warn('[PRC] No container found with data-prc-calculator="' + id + '". Ensure the section HTML is loaded before calling register().');
        return;
      }

      containers[id] = container;

      // Wire everything up
      wireCalculator(id);
    },

    // Expose modules
    validate: validate,
    ui: ui,
    session: session,
    api: api,
    analytics: analytics,
    spam: spam,
    format: format,

    /**
     * Get a registered calculator by id. Useful for cross-calculator communication.
     * @param {string} id
     * @returns {Object|undefined}
     */
    get: function (id) {
      return registry[id];
    },

    /**
     * Get the container element for a registered calculator.
     * @param {string} id
     * @returns {HTMLElement|undefined}
     */
    getContainer: function (id) {
      return containers[id];
    },

    /**
     * Configure the API connection.
     * @param {Object} opts - { apiBase: string, apiKey: string }
     */
    configure: function (opts) {
      if (opts.apiBase) API_BASE = opts.apiBase;
      if (opts.apiKey) API_KEY = opts.apiKey;
    }
  };

  /* ==========================================================================
     Expose Global + flush shim queue
     ==========================================================================
     The snippets/calculator-framework.liquid loader installs a stand-in
     window.PRC before this script runs (the framework is deferred but
     each calc-* section's inline <script> executes at parse time and
     calls PRC.register immediately). Replace the shim with the real
     API, then replay any queued register/configure calls in arrival
     order so calculators wire up the moment the framework binds.
     ========================================================================== */

  var pendingShimQueue = (window.PRC && window.PRC._isShim && window.PRC._q) || null;
  window.PRC = PRC;
  if (pendingShimQueue && pendingShimQueue.length) {
    for (var __qi = 0; __qi < pendingShimQueue.length; __qi++) {
      var __name = pendingShimQueue[__qi][0];
      var __args = pendingShimQueue[__qi][1];
      if (typeof PRC[__name] === 'function') {
        try { PRC[__name].apply(PRC, __args); }
        catch (e) { console.error('[PRC] queued ' + __name + ' threw:', e); }
      }
    }
  }

  /* ==========================================================================
     Keyboard: Escape to dismiss toast globally
     ========================================================================== */

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      hideToast();
    }
  });

})();
